import { Component, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { AnalyticsService, getDefaultDateRange, StatCard, PopularExperience } from '@shared/services/analytics.service';
import { ActivatedRoute } from '@angular/router';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { Learnspace } from '@shared/models/learnspace.model';
import { Observable, Subscription } from 'rxjs';
import { PaginatedScrollService } from '@shared/services/paginated-scroll.service';
import { LoadState } from '../loader-directive/loader.module';
import { formatPercent } from '@angular/common';
import { courseLabel } from '@shared/utils/wording.utils';

@Component({
  templateUrl: './learnspace-analytics.component.html',
  styleUrls: ['./learnspace-analytics.component.scss']
})
export class LearnspaceAnalyticsComponent implements OnInit, OnDestroy {

  public learnspace: Learnspace;
  public popularExperiences: PopularExperience[];
  private page = 0;
  private pageSize = 5;
  private _scrollSubs: Subscription;
  public loadStatus = LoadState.pending;
  date_from: Date;  // TODO: Default value 1 year
  date_to: Date;
  public scoringEnabledLearnspace: boolean;

  public statsCards: StatCard[];
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _courseAPIService: CoursesAPIService,
    private _analyticsService: AnalyticsService,
    private _elRef: ElementRef,
    private _paginatedScrollService: PaginatedScrollService
  ) {

  }

  ngOnInit(): void {
    const defaultRange = getDefaultDateRange();
    this.date_from = defaultRange.date_from;
    this.date_to = defaultRange.date_to;
    this._activatedRoute.paramMap.subscribe(params => {
      const learnspaceId = params.get('lsId');
      this._courseAPIService.getLearnspaceById(learnspaceId).subscribe(async (learnspace) => {
        this.learnspace = learnspace;
        this.scoringEnabledLearnspace = await this._courseAPIService.learnspaceHasScoringEnabled(learnspaceId).toPromise();
        this.updateLearnspaceStats();
        this.popularExperiences = await this.loadPopularExperiences().toPromise();
        await this.fillContainerWithExperiences();
        this.initScroll();
      });
    });
    this._activatedRoute.queryParams.subscribe(async params => {
      let dateRangeChange = false;
      if (params.dateFrom && this.date_from && this.date_from.getTime() !== parseInt(params.dateFrom)) {
        this.date_from = new Date(parseInt(params.dateFrom))
        dateRangeChange = true;
      }
      if (params.dateTo && this.date_to && this.date_to.getTime() !== parseInt(params.dateTo)) {
        this.date_to = new Date(parseInt(params.dateTo))
        dateRangeChange = true;
      }
      if (params.timePickerMode && params.timePickerMode === 'forever') {
        this.date_from = null;
        this.date_to = null;
        dateRangeChange = true;
      }
      if (this.learnspace && dateRangeChange) {
        this.updateLearnspaceStats();
        this.page = 0;
        this.popularExperiences = await this.loadPopularExperiences().toPromise();
        await this.fillContainerWithExperiences();
      }
    })
  }
  onTimePeriodChange(range: { date_from: Date, date_to: Date }) {
    this.date_from = range.date_from;
    this.date_to = range.date_to;
    this.updateLearnspaceStats();
  }

  async updateLearnspaceStats(): Promise<void> {
    this.loadStatus = LoadState.loading;
    let learnspaceStats;
    try {
      learnspaceStats = await this._analyticsService.getLearnspaceStats(this.learnspace.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
    }
    catch (e) {
      this.loadStatus = LoadState.error;
    }
    const xpStatCard = {
      icon: 'fa-circle-plus',
      label: `<span class="figure">${learnspaceStats?.activeExperiences || 0} </span>&nbsp;<span class="label"> active XP</span>`,
      sublabel: `${this.learnspace.experienceCount || 0} ${courseLabel(this.learnspace.mode, true)} in total`
    }

    const visitorStatCard = {
      icon: 'fa-users',
      label: `<span class="figure">${learnspaceStats?.userCount || 0} </span>&nbsp;<span class="label">${(!isNaN(learnspaceStats?.userCount) && learnspaceStats?.userCount) > 1 ? ' Visitors' : ' Visitor'}</span>`,
      sublabel: 'including ' + (learnspaceStats?.anonymousUsersCount || 0) + ' guests'
    }
    const sessionStatCard = {
      icon: 'fa-eye',
      label: `<span class="figure">${learnspaceStats?.sessionCount} </span>&nbsp;<span class="label"> sessions</span>`,
      sublabel: `${Math.floor((learnspaceStats?.sessionsPerVisitor || 0))} sessions per visitor`
    }


    const statCards = [xpStatCard, visitorStatCard, sessionStatCard]
    if (learnspaceStats && learnspaceStats.totalAIVoiceMinutes > 0) {
      const aiStatCard = {
        icon: 'fa-message',
        label: `<span class="figure"> ${Math.ceil((learnspaceStats?.totalAIVoiceMinutes || 0))}</span>&nbsp;<span class="label"> AI Voice Min </span>`,
        sublabel: `${Math.ceil((learnspaceStats?.avgAIVoiceMinutes || 0))} min/session`
      }
      statCards.push(aiStatCard)
    }
    if (this.scoringEnabledLearnspace) {
      statCards.push({
        icon: 'fa-circle-check',
        label: `<span class="figure">${formatPercent(learnspaceStats?.passedRatio || 0, 'en-US')}</span>&nbsp;<span class="label">passed</span>`,
        sublabel: ``
      })
    }

    this.loadStatus = LoadState.completed;
    this.statsCards = statCards;
  }
  // PaginatedScrollService.attach() only fires on scroll events, so if the initial content doesn't fill
  // the container no scroll ever triggers. PaginatedScrollController has _autoFill() which handles this,
  // but it is only available when using createController() — attach() that cannot be used here because 
  // the API signature is different (different query filter, total expected in response)
  private async fillContainerWithExperiences(): Promise<void> {
    const container = this._elRef.nativeElement;
    while (container.scrollHeight <= container.clientHeight) {
      this.page++;
      const next = await this.loadPopularExperiences(this.pageSize, this.page * this.pageSize).toPromise();
      if (!next || next.length === 0) break;
      this.popularExperiences = this.popularExperiences.concat(next);
      await new Promise(r => setTimeout(r));
    }
  }

  initScroll(): void {
    this._scrollSubs = this._paginatedScrollService
      .attach(this._elRef.nativeElement, undefined, 0.1)
      .subscribe(async () => {
        this.page++;
        const nextPopularExperiences = await this.loadPopularExperiences(this.pageSize, this.page * this.pageSize).toPromise();
        if (nextPopularExperiences && nextPopularExperiences.length > 0) {
          this.popularExperiences = this.popularExperiences.concat(nextPopularExperiences);
        } else {
          this._scrollSubs?.unsubscribe();
        }
      });
  }
  loadPopularExperiences(limit: number = 5, offset: number = 0): Observable<PopularExperience[]> {
    return this._analyticsService.getLearnspacePopularExperiences(this.learnspace.id, { dateFrom: this.date_from, dateTo: this.date_to, limit: limit, offset: offset })
  }

  ngOnDestroy(): void {
    this._scrollSubs?.unsubscribe();
  }

  openHelp(): void {
    window.open('http://help.spaces.wondavr.com/en/articles/3294026-analytics-dashboard');
  }
}
